import { useState } from "react";
import { Link } from "react-router-dom";
import { Arr } from "../../components/CTA";

/* ───────────────────────── Interactive audit log ─────────────────────── */

const ENTRIES = [
  {
    ts: "2026-03-14 09:02:11",
    actor: "system",
    title: "Document received",
    detail: [
      { type: "p", text: "File invoice_INV-20418.pdf, 248 KB, received into tenant \"acme-finance\", batch vendor-batch-07. Queued for processing." },
      { type: "seal" },
    ],
  },
  {
    ts: "2026-03-14 09:02:14",
    actor: "model",
    title: "Classified as Supplier invoice",
    detail: [
      { type: "kv", rows: [
        ["Confidence", "98.7%"],
        ["Model version", "inv-clf-4.2"],
      ] },
      { type: "p", text: "Other candidates considered:" },
      { type: "kv", rows: [
        ["Purchase order", "0.9%"],
        ["Delivery note", "0.3%"],
      ] },
    ],
  },
  {
    ts: "2026-03-14 09:02:15",
    actor: "model",
    title: "Boundaries checked, single document",
    detail: [
      { type: "p", text: "One document detected across 2 pages, page range 1 to 2. No splitting required." },
    ],
  },
  {
    ts: "2026-03-14 09:02:19",
    actor: "model",
    title: "Fields extracted",
    detail: [
      { type: "p", text: "5 fields, model version inv-ext-6.1." },
      { type: "fields", rows: [
        ["Invoice number", "INV-20418", "99.1%", "ok"],
        ["Vendor", "Acme Supplies Ltd", "98.4%", "ok"],
        ["Total amount", "GBP 12,480.00", "64.0%", "warn"],
        ["Due date", "30 April 2026", "96.5%", "ok"],
        ["Project code", "PRJ-7741", "95.0%", "ok"],
      ] },
    ],
  },
  {
    ts: "2026-03-14 09:02:19",
    actor: "system",
    title: "Field flagged for review",
    detail: [
      { type: "p", text: "Total amount, confidence 64.0%, below tenant threshold of 85%. Routed to the review queue." },
    ],
  },
  {
    ts: "2026-03-14 09:14:37",
    actor: "person",
    actorName: "A. Sharma (Reviewer)",
    title: "Field corrected",
    detail: [
      { type: "p", text: "Total amount confirmed as GBP 12,480.00 after review of the source region." },
      { type: "quote", text: "Total due, not the subtotal." },
    ],
  },
  {
    ts: "2026-03-14 09:14:37",
    actor: "system",
    title: "Correction recorded as training signal",
    detail: [
      { type: "p", text: "Correction queued for model inv-ext. Training signal id sig-77a204." },
    ],
  },
  {
    ts: "2026-03-14 09:15:02",
    actor: "system",
    title: "PII anonymised on export",
    detail: [
      { type: "p", text: "Supplier bank account number and contact email masked in the delivered payload, per tenant policy pii-mask-v2." },
    ],
  },
  {
    ts: "2026-03-14 09:15:02",
    actor: "system",
    title: "Delivered",
    detail: [
      { type: "p", text: "Exported as JSON to system of record \"Finance-ERP\". Export id exp-88142." },
      { type: "seal" },
    ],
  },
];

function ActorTag({ kind, name }) {
  const labels = { system: "SYSTEM", model: "MODEL", person: "PERSON" };
  return (
    <span className={`alg__chip alg__chip--${kind}`} aria-label={`Actor: ${labels[kind]}${name ? ", " + name : ""}`}>
      <span className="alg__chip-dot" aria-hidden="true" />
      {labels[kind]}
      {name && <em>{name}</em>}
    </span>
  );
}

function Detail({ block }) {
  if (block.type === "p") return <p className="alg__p">{block.text}</p>;
  if (block.type === "quote") return <blockquote className="alg__quote">"{block.text}"</blockquote>;
  if (block.type === "seal")
    return (
      <div className="alg__seal" aria-hidden="false">
        <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M3 8l3 3 7-7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Entry sealed.
      </div>
    );
  if (block.type === "kv")
    return (
      <div className="alg__kv">
        {block.rows.map(([k, v]) => (
          <div key={k}><span>{k}</span><b>{v}</b></div>
        ))}
      </div>
    );
  if (block.type === "fields")
    return (
      <div className="alg__fields">
        {block.rows.map(([k, v, c, s]) => (
          <div className={`alg__field alg__field--${s}`} key={k}>
            <span className="alg__field-k">{k}</span>
            <span className="alg__field-v">{v}</span>
            <span className={`alg__field-c alg__field-c--${s}`}>{c}</span>
          </div>
        ))}
      </div>
    );
  return null;
}

function AuditLog() {
  // Entry 4 (index 3) starts expanded
  const [open, setOpen] = useState(() => new Set([3]));

  const toggle = (i) => {
    setOpen((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  };
  const expandAll = () => setOpen(new Set(ENTRIES.map((_, i) => i)));
  const collapseAll = () => setOpen(new Set());

  return (
    <aside className="alg" aria-labelledby="alg-title" data-testid="audit-log">
      <header className="alg__head">
        <h3 id="alg-title" className="alg__title">One document, its whole trail</h3>
        <p className="alg__intro">
          The complete audit trail for a single invoice, exactly as the pipeline recorded it. Click any entry to see what was captured.
        </p>
        <div className="alg__controls">
          <button type="button" className="alg__ctrl" onClick={expandAll} data-testid="alg-expand-all">Expand all</button>
          <button type="button" className="alg__ctrl" onClick={collapseAll} data-testid="alg-collapse-all">Collapse all</button>
        </div>
      </header>

      <ol className="alg__list">
        {ENTRIES.map((e, i) => {
          const isOpen = open.has(i);
          return (
            <li key={i} className={`alg__item${isOpen ? " is-open" : ""}`} data-testid={`alg-entry-${i + 1}`}>
              <button
                type="button"
                className="alg__row"
                aria-expanded={isOpen}
                aria-controls={`alg-detail-${i}`}
                onClick={() => toggle(i)}
                data-testid={`alg-toggle-${i + 1}`}
              >
                <span className="alg__node" aria-hidden="true" />
                <span className="alg__ts">{e.ts}</span>
                <ActorTag kind={e.actor} name={e.actorName} />
                <span className="alg__title-cell">{e.title}</span>
                <svg className="alg__caret" width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <div
                id={`alg-detail-${i}`}
                className="alg__detail"
                role="region"
                aria-labelledby={`alg-detail-${i}`}
                hidden={!isOpen}
              >
                <div className="alg__detail-inner">
                  {e.detail.map((b, j) => <Detail key={j} block={b} />)}
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      <p className="alg__footer">
        Every entry is append-only and sealed. The trail cannot be edited after the fact. Nothing here was assembled, each line was written by the step that produced it.
      </p>
    </aside>
  );
}

/* ────────────────────────────── Page ────────────────────────────── */

export default function AuditTrailByProduct() {
  return (
    <article className="article">
      <header className="article__head">
        <div className="container container--narrow">
          <nav className="crumb" aria-label="Breadcrumb">
            <Link to="/">Docket</Link> / <Link to="/insights">Insights</Link> / How to make the audit trail a by-product
          </nav>
          <p className="article__eyebrow">Compliance &nbsp;·&nbsp; November 2025 &nbsp;·&nbsp; 5 min read</p>
          <h1 className="article__title">How to make the audit trail a by-product of the work</h1>
          <p className="article__standfirst">
            An audit trail assembled after the fact is a reconstruction. One recorded as the work happens is evidence. The difference is everything.
          </p>
          <hr className="article__rule" />
        </div>
      </header>

      <div className="container container--narrow article__body">
        <p>
          For most teams, the audit trail is a thing you build. Usually late. Usually under pressure, a week before the auditor arrives or a day after a customer asks an awkward question. Someone opens a spreadsheet, pulls together what logs exist, asks a few people what they remember, and assembles a record of what happened.
        </p>
        <p>
          That record is not worthless. But it is the wrong kind of record, and it is worth being precise about why.
        </p>
        <p>
          A good audit trail is not built at all. It is left behind by the work, the way footprints are left by walking. If that sounds like a small distinction, it is not. It is the difference between a trail you can trust and a trail you can only hope is right.
        </p>

        <h2 className="article__sub">Why the assembled trail is the weak one</h2>
        <p>An audit trail put together after the fact has three problems, and all three are built in.</p>
        <p>
          It is incomplete. You can only reconstruct what someone thought to record at the time, and at the time, nobody knew which detail would matter. The steps that seemed routine were not logged, because routine steps do not feel like evidence until someone asks about one.
        </p>
        <p>
          It trusts memory. Reconstruction leans on what people recall and what partial logs imply. Memory is confident and wrong in roughly equal measure, and "as far as we can tell" is not a phrase that belongs in an audit.
        </p>
        <p>
          And it is editable. A record assembled in a spreadsheet after the fact can be changed after the fact, by anyone, with no trace. It may be perfectly honest. It is just not, in any technical sense, evidence. It is a story told about the work, and a story is only as reliable as the storyteller.
        </p>
        <p>
          An assembled trail answers the auditor's question. It does not give you assurance. Those are not the same thing.
        </p>

        <h2 className="article__sub">The shift: doing the work and recording it are one act</h2>
        <p>Here is the better idea, and it is structural, not clever.</p>
        <p>
          The audit trail should not be a separate activity. It should be a by-product of the work itself. Every time the system does something to a document, classifies it, extracts a field, scores its own confidence, flags something for review, accepts a correction, exports a result, that action is recorded the instant it happens, by the step that performed it.
        </p>
        <p>
          When the trail is built this way, nobody maintains it. There is no logging task, no spreadsheet, no reconstruction. The trail simply accumulates, because the work happened and the work writes itself down. Proving the work and doing the work stop being two jobs. They become one.
        </p>
        <p>
          This is the whole move. The reason the assembled trail is weak is that proving was separated from doing, so proving was always late, always partial, always a cost. Put them back together and the trail becomes free and complete at the same time.
        </p>

        <h2 className="article__sub">What "by-product" actually requires</h2>
        <p>
          For an audit trail to be a genuine by-product, rather than a separate burden in disguise, the system underneath it has to do four things. They are worth checking for by name.
        </p>
        <ul className="article__list">
          <li><strong>Capture every event automatically.</strong> Not the events someone remembered to instrument. Every meaningful action: classification, extraction, each confidence score, every review decision, every correction, every export. If capture depends on a person remembering, it is not a by-product.</li>
          <li><strong>Record who, what and when on every entry.</strong> Each line should say whether it was the model or a person, which user if it was a person, and the exact time. An entry without an actor and a timestamp is an anecdote.</li>
          <li><strong>Be append-only and tamper-evident.</strong> Entries are written once and sealed. Nothing can be edited or removed after the fact without that being visible. This is what turns a record into evidence.</li>
          <li><strong>Stay tied to the real document and the real data.</strong> The trail should attach to the actual document and the actual extracted values, not sit in a separate system that has to be cross-referenced and trusted on faith.</li>
        </ul>
        <p>
          If all four hold, the trail is a by-product. If any one is missing, you are quietly back to assembling, and you tend to find that out at the worst possible moment.
        </p>

        <AuditLog />

        <p className="article__caption">
          Nobody created this record. It is what the pipeline wrote down while it worked. Received to delivered, model and person both, every step stamped with who, what and when.
        </p>
        <p className="article__caption article__caption--foot">
          An illustration with one sample document. The structure is real: in Docket each pipeline stage records its own actions, with actor, timestamp and detail, to an append-only, tamper-evident trail.
        </p>

        <h2 className="article__sub">It is worth more than compliance</h2>
        <p>
          Once the trail is a faithful, automatic record of operations, something useful happens. It stops being only a compliance artifact.
        </p>
        <p>
          The same record that satisfies an auditor also tells you which model version produced a bad extraction, and when. It shows you exactly which fields your reviewers correct most often, which is a map of where the model needs work. It holds the corrections themselves, which are training signal. It lets you answer a customer's question about their document with a fact instead of a reassurance.
        </p>
        <p>
          Compliance is one consumer of a good operational record. It is not the reason to have one. A system that records itself honestly is easier to run, easier to debug and easier to trust, and the audit happens to fall out of it for free.
        </p>

        <h2 className="article__sub">The honest version</h2>
        <p>
          A by-product audit trail does not do your compliance for you. It does not interpret a regulation, decide what your policy should be, or judge whether an exception was handled correctly. Those are human responsibilities, and they remain human responsibilities.
        </p>
        <p>
          What it does is remove the part of compliance that was never really judgement: the reconstruction, the assembly, the clerical effort of proving that the work happened. It makes the evidence free and trustworthy, so the human effort can go where it belongs, into interpretation and policy and the genuinely hard calls, rather than into rebuilding a record the system should have kept all along.
        </p>

        {/* Closing CTA */}
        <section className="article__cta" data-testid="article-cta">
          <div>
            <h3>Watch the trail build on your own documents.</h3>
            <p>A Docket pilot runs your real document types through the pipeline. Every classification, score, review and export is recorded as it happens, in four to six weeks.</p>
          </div>
          <Link to="/contact" className="article__cta-btn" data-testid="article-cta-btn">
            Request a pilot <Arr />
          </Link>
        </section>

        <div className="article__backwrap">
          <Link to="/insights" className="txtlink" data-testid="article-back">
            <svg className="arr" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ transform: "rotate(180deg)" }}>
              <path d="M3 8h9m0 0L8 4m4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to all Insights
          </Link>
        </div>
      </div>
    </article>
  );
}
